import {
  CheckoutTerms,
  RegisterTerms,
  purchaseSeatsLanguages,
  purchaseGiftCardLanguages,
  redeemGiftCardLanguages,
} from "./helpers";
import { routes } from "@/global/routes";
import { constants, vendorUUIDs } from "../../constants";
import type { TeamConfig } from "@/global/hooks/useTeamConfig";
import { translations } from "./translations";
import { lighten } from "@mui/material";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/LOGO_PlaceDesArts_H_RGB_txt_K._1200.png";
const BannerImage =
  "https://ss-leagues.s3.us-east-2.amazonaws.com/CHEQUE_CADEAU_GIFT_CARD_tile_2000x2000_grey_no+logo.jpg";
const FaviconImage = "https://www.placedesarts.com/favicon/favicon.ico";

const config: TeamConfig = {
  teamSchema: "placedesarts_giftcard",
  browserTitle: "Place des Arts Gift Cards",
  packages: false,
  defaultRoute: routes.giftCard,
  phoneNumber: "",
  teamName: "Place des Arts",

  // seat map configs
  seatMapConfigs: {
    venueId: "",
    mapId: "",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: true,
    redirectGiftCardUrl: routes.giftCard,
    isCheckoutSaveCardEnabled: true,
    maxGiftPurchaseCountAllowed: 8,

    enableConfirmationModal: true,

    footer: {
      backgroundColor: "#D4D4D8",
      textColor: "#050707",
      dividerColor: lighten("#050707", 0.7),
    },
  },

  // disabled routes
  disabledRoutes: [routes.purchaseSeats],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    checkoutTimerEnabled: false,
    showFrontendTotalAmount: false,
    showFrontendServiceFee: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
    checkoutTimerEnabled: false,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: false,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  isSaveCardAllowed: true,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // call refresh inventory when selecting games
  refreshInventoryWhenEventClick: false,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/pda/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    logoNameImage: "",
    findGamesBannerImage: "",
    seatMap3dPreviewImage: "",
    faviconImage: FaviconImage,
    authBackgroundImage: BannerImage,
    checkoutStatusPageImage: BannerImage,
    giftCardStandalonePageImage: BannerImage,
    logoImage: LogoImage + "?width=156&height=76",
  },

  colors: {
    primary: "#e86a10",
  },

  howItWorks: [],
  faqs: [],

  i18nConfig: {
    lng: constants.locales.fr,
    resources: translations,
  },

  uiConfigs: {
    bannerImage: {
      title: "Choose Your Games",
      isBackgroundBasedBanner: true,
    },
    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },
    bottomBar: {
      showGameSelectionProgress: false,
    },
    hamburgerMenu: {
      purchaseHistoryEnabled: false,
    },
  },

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <CheckoutTerms />,
    registrationTermsAndCondition: <RegisterTerms />,
    purchaseSeats: purchaseSeatsLanguages,
    purchaseGiftCard: purchaseGiftCardLanguages,
    redeemGiftCard: redeemGiftCardLanguages,

    giftCard: {
      title: "Place des Arts Gift Cards",
      createGiftCard: {
        description:
          "Share the magic of Place des Arts with a friend or loved one. Upon purchase of a gift card, a unique code will be generated for the intended recipient to redeem. Once redeemed, the gift card is not refundable.",
        recipient: {
          sendNowFalse: {
            description:
              "If an intended recipient is not specified, once purchased, you will have to forward the unique code to the recipient. Upon your recipient receiving their gift card, they will be prompted to enter their unique code to redeem the gift card. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Place des Arts shows.",
          },
          sendNowTrue: {
            description:
              "The Recipient will be sent an email with the unique code for them to redeem their gift card. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Place des Arts shows.",
          },
        },
      },

      redeemGiftCard: {
        loggedIn: {
          description:
            "Please input the case sensitive code provided to redeem the value of the gift card to the Place des Arts account above.",
        },
        logout: {
          description:
            "Please input the case sensitive code provided to redeem the value of the gift card to the Place des Arts account.",
        },
        success: {
          description: "has been added to your Place des Arts account!",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
